import React, { useEffect, useState } from 'react';
import { translate } from 'components/Translation';
import CardBody from 'components/Card/CardBody';
import style from 'assets/jss/material-dashboard-pro-react/views/contactUs/trackingComplaintStyle';
import { HeadingLBold } from 'components/Typography/Typography';
import { makeStyles } from '@material-ui/core';
import { ContactService } from 'services/contact/contact';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { finalize } from 'rxjs/operators';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { Ticket } from 'utils/api/models/troubleTicketing/ticket';
import TrackTicket from './subcomponents/TrackTicket';
import ShowStatus from './subcomponents/ShowStatus';
import { resolveTicketStatus } from 'views/ContactUs/helpers/resolveTicketStatus';
import { useParams } from 'react-router-dom';
import { upIdSelector } from 'redux/modules/session/selectors';
import { useSelector } from 'react-redux';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { getLanguage } from 'utils/language/language';

// @ts-ignore
const useStyles = makeStyles(style);

enum STEPS {
	TRACK_TICKET = 0,
	SHOW_STATUS,
	ERROR,
}

const TicketTracking: React.FC<{ ticketNumber: string }> = ({ ticketNumber }) => {
	const [ticketId, setTicketId] = useState<string>(ticketNumber ?? '');
	const [status, setStatus] = useState<types.troubleTicketing.finalStatus | null>(null);

	const [loading, setLoading] = useState<boolean>(false);
	const [step, setStep] = useState<STEPS>(STEPS.TRACK_TICKET);
	const classes = useStyles();
	const [ticketData, setTicketData] = useState<Ticket | null>(null);
	const accId = useSelector(upIdSelector);

	const pushErrorEvent = () => {
		setDataLayer('track_ticket_error', getLanguage(), null, accId, null, {
			error_type: 'ticket detail',
			ticket_num: ticketId,
		});
	};

	const track = () => {
		setDataLayer('track_ticket', getLanguage(), null, accId, null, {
			type: 'button',
			link_name: `button:${translate('contact.tickettracking.buttonLabel')}`,
		});
		if (/^IM/.test(ticketId)) {
			retrieveOsmIncident();
		} else if (/^SD/.test(ticketId)) {
			retrieveOsmInteraction();
		} else {
			pushErrorEvent();
			setStep(STEPS.ERROR);
		}
	};

	const retrieveOsmIncident = () => {
		setLoading(true);
		ContactService.retrieveOsmIncident({ incidentId: ticketId })
			.pipe(finalize(() => setLoading(false)))
			.subscribe(
				(incident) => {
					setTicketData(incident);
					showTrackTicketData(incident, 'INCIDENT');
				},
				() => {
					pushErrorEvent();
					setStep(STEPS.ERROR);
				}
			);
	};

	const retrieveOsmInteraction = () => {
		setLoading(true);
		ContactService.retrieveOsmInteraction({ interactionId: ticketId })
			.pipe(finalize(() => setLoading(false)))
			.subscribe(
				(interaction) => {
					setTicketData(interaction);
					showTrackTicketData(interaction, 'INTERACTION');
				},
				() => {
					pushErrorEvent();
					setStep(STEPS.ERROR);
				}
			);
	};

	const trackAnother = () => {
		setStep(STEPS.TRACK_TICKET);
		setTicketId('');
		setTicketData(null);
	};

	const showTrackTicketData = (ticket: Ticket, typeOfTicket: types.troubleTicketing.typeOfTicket) => {
		const finalStatus = resolveTicketStatus(ticket, typeOfTicket);
		if (finalStatus) {
			setStatus(finalStatus[2]);
			setDataLayer('ticket_detail_view', getLanguage(), null, accId, null, {
				type: 'ticket detail',
				ticket_num: ticketId,
				source: 'query ticket track',
			});
			setStep(STEPS.SHOW_STATUS);
		} else {
			setStep(STEPS.ERROR);
		}
	};

	useEffect(() => {
		if (ticketNumber) {
			track();
		}
	}, []);

	return (
		<>
			<ConditionalRender
				show={loading}
				onTrue={() => (
					<div className={classes.loaderWrapper}>
						<CustomCircularProgress loading text="" transparent />
					</div>
				)}
			/>
			<CardBody className={classes.cardBody}>
				<ConditionalRender
					show={step === STEPS.TRACK_TICKET}
					onTrue={() => (
						<HeadingLBold disableMargin>{translate('contact.tickettracking.header')}</HeadingLBold>
					)}
				/>
				<div className={classes.ticketTrackingContentWrapper}>
					<ConditionalRender
						show={step === STEPS.TRACK_TICKET || step === STEPS.ERROR}
						onTrue={() => (
							<TrackTicket
								ticketId={ticketId}
								setTicketId={setTicketId}
								track={track}
								error={step === STEPS.ERROR}
							/>
						)}
					/>
					<ConditionalRender
						show={step === STEPS.SHOW_STATUS}
						onTrue={() => (
							<ShowStatus
								ticketData={ticketData}
								ticketId={ticketId}
								status={status}
								onClose={trackAnother}
							/>
						)}
					/>
				</div>
			</CardBody>
		</>
	);
};
export default TicketTracking;
